import {create} from 'zustand'
import {Order} from 'types/types.js'
import time from 'server/utils/time'
import filter from 'src/utils/filter'
import socket from 'src/socketio/socketio'
import _ from 'lodash'
import {emit} from 'src/socketio/socketEmit'

export interface OrderQuery {
    title: string
    operator: '=' | '==' | '!=' | '>' | '<'
    text: string
}

interface CustomerStore {
    orders: Record<string, Order>
    filteredOrders: Record<string, Order>
    selectedOrders: string[]
    ordersLoading: boolean
    orderStartDate: string
    orderEndDate: string
    orderQueries: OrderQuery[]
    getOrders: (ids?: string[]) => Promise<void>
    setSelectedOrders: (selectedOrders: string[]) => void
    setOrderQueries: (orderQueries: OrderQuery[]) => void
    setOrderDates: (orderStartDate: string, orderEndDate: string) => void
}

const useCustomerStore = create<CustomerStore>((set, get) => ({
    orders: {},
    filteredOrders: {},
    selectedOrders: [],
    ordersLoading: true,
    orderStartDate: time.today(),
    orderEndDate: time.today(),
    orderQueries: [],
    getOrders: async (ids?: string[]) => {
        set({ordersLoading: !ids})

        const {data: updatedOrders} = await emit<Order[]>(`orders.getCustomerOrders`, {
            ids,
            startDate: get().orderStartDate,
            endDate: get().orderEndDate
        })

        let ordersObject = {}
        const updatedOrdersObject = updatedOrders.reduce((acc, order) => {
            acc[order._id] = order
            return acc
        }, {})

        let orderHasChanged = false

        if (ids) {
            ordersObject = get().orders
            const selectedOrders = get().selectedOrders

            // update orders
            ids.map((id) => {
                const oldOrder = ordersObject[id]
                const order = updatedOrdersObject[id]

                if (order && !order.removed) {
                    ordersObject[id] = order
                    orderHasChanged = true
                } else if (oldOrder) {
                    const selectedIndex = selectedOrders.indexOf(id)
                    selectedOrders.splice(selectedIndex, 1)

                    delete ordersObject[id]

                    orderHasChanged = true
                }
            })
        } else {
            orderHasChanged = true
            ordersObject = updatedOrdersObject
        }

        if (orderHasChanged) {
            const filteredOrders = filter.orders(ordersObject, {}, get().orderQueries, get().orderStartDate, get().orderEndDate) as Record<string, Order>

            set({
                orders: ordersObject,
                filteredOrders,
                selectedOrders: get().selectedOrders.filter((id) => !!filteredOrders[id]),
                ordersLoading: false
            })
        }
    },
    setSelectedOrders: (selectedOrders: string[]) => set({selectedOrders}),
    setOrderQueries: (orderQueries: OrderQuery[]) => {
        const {orders} = get()

        set({orderQueries})
        const filteredOrders = filter.orders(orders, {}, orderQueries, get().orderStartDate, get().orderEndDate) as Record<string, Order>
        set({filteredOrders, selectedOrders: []})
    },
    setOrderDates: (orderStartDate: string, orderEndDate: string) => {
        set({orderStartDate, orderEndDate})
        get().getOrders()
    }
}))

let updateOrderIds: string[] = []
let updateTimeout: ReturnType<typeof setTimeout> | null = null

const updateOrders = (orderIds: string | string[], routeIds: string[], forceUpdate: boolean = false) => {
    if (!sessionStorage.token) return

    orderIds = !orderIds ? [] : typeof orderIds === 'string' ? [orderIds] : orderIds

    console.log(`updateOrders force: ${forceUpdate}`)

    if (forceUpdate) {
        useCustomerStore.getState().getOrders(orderIds)
    } else {
        if (updateTimeout) {
            updateOrderIds = _.uniq([...updateOrderIds, ...orderIds])
        } else {
            useCustomerStore.getState().getOrders(_.uniq([...updateOrderIds, ...orderIds]))
            updateOrderIds = []

            updateTimeout = setTimeout(() => {
                updateTimeout = null

                if (updateOrderIds.length) {
                    useCustomerStore.getState().getOrders([...updateOrderIds])
                    updateOrderIds = []
                }
            }, 5000)
        }
    }
}

socket.on('updateOrder', updateOrders)

export default useCustomerStore
